/**
 * teamsService
 *
 * Client-side Teams store backed by localStorage, scoped by organisation.
 *
 * No backend Team entity exists yet — the Figma "Teams" page is a pure UI
 * concept that groups users + talk groups under a named team with a
 * designated "home" talk group. Once the backend grows a Team resource,
 * this file is the single place that needs to be swapped to real HTTP
 * calls (the page uses the async API exposed here).
 *
 * Storage key:  davinci.teams.v1.<orgId>
 * Record shape: { id, name, talkGroupIds: number[], memberIds: number[], homeGroupId: number|null, createdAt, updatedAt }
 */

const VERSION = 'v1';

function storageKey(orgId) {
  return `davinci.teams.${VERSION}.${orgId ?? 'default'}`;
}

function readAll(orgId) {
  try {
    const raw = localStorage.getItem(storageKey(orgId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(orgId, teams) {
  try {
    localStorage.setItem(storageKey(orgId), JSON.stringify(teams));
  } catch {
    /* quota / disabled storage — ignore */
  }
}

function uid() {
  return `tm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

const teamsService = {
  async listTeams(orgId) {
    return readAll(orgId);
  },

  async createTeam(orgId, data) {
    const teams = readAll(orgId);
    const now = new Date().toISOString();
    const record = {
      id: uid(),
      name: (data.name || '').trim(),
      talkGroupIds: Array.isArray(data.talkGroupIds) ? [...data.talkGroupIds] : [],
      memberIds:    Array.isArray(data.memberIds)    ? [...data.memberIds]    : [],
      homeGroupId:  data.homeGroupId ?? null,
      createdAt: now,
      updatedAt: now,
    };
    teams.push(record);
    writeAll(orgId, teams);
    return record;
  },

  async updateTeam(orgId, id, data) {
    const teams = readAll(orgId);
    const idx = teams.findIndex((t) => t.id === id);
    if (idx === -1) throw new Error('Team not found');
    const next = {
      ...teams[idx],
      ...data,
      talkGroupIds: Array.isArray(data.talkGroupIds) ? [...data.talkGroupIds] : teams[idx].talkGroupIds,
      memberIds:    Array.isArray(data.memberIds)    ? [...data.memberIds]    : teams[idx].memberIds,
      updatedAt: new Date().toISOString(),
      id: teams[idx].id,
    };
    teams[idx] = next;
    writeAll(orgId, teams);
    return next;
  },

  async deleteTeam(orgId, id) {
    const teams = readAll(orgId).filter((t) => t.id !== id);
    writeAll(orgId, teams);
    return true;
  },
};

export default teamsService;

